"use client"

import * as React from "react"
import { cn } from "@/lib/utils"
import { Button } from "../button"
import { Paperclip } from "lucide-react"
import { Send } from "lucide-react"

interface ChatInputProps extends React.HTMLAttributes<HTMLDivElement> {
  onSend?: (message: string) => void
  onAttach?: () => void
}

const ChatInput = React.forwardRef<HTMLDivElement, ChatInputProps>(({ onSend, onAttach, className, ...props }, ref) => {
  const [message, setMessage] = React.useState("")

  const handleSend = () => {
    if (message.trim()) {
      onSend?.(message)
      setMessage("")
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <div ref={ref} className={cn("mt-6 flex items-center gap-3", className)} {...props}>
      <Button
        onClick={onAttach}
        variant="ghost"
        size="icon"
        className="bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-full flex-shrink-0"
        aria-label="Attach file"
      >
        <Paperclip className="w-5 h-5" />
      </Button>
      <div className="relative flex-grow">
        <input
          className="w-full pl-5 pr-12 py-3 rounded-full bg-white border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent text-gray-800 placeholder-gray-500"
          placeholder="Ask me anything..."
          type="text"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
        />
      </div>
      <Button
        onClick={handleSend}
        disabled={!message.trim()}
        variant="default"
        size="icon"
        className="bg-blue-600 hover:bg-blue-700 text-white rounded-full flex-shrink-0"
        aria-label="Send message"
      >
        <Send className="w-5 h-5" />
      </Button>
    </div>
  )
})
ChatInput.displayName = "ChatInput"

export { ChatInput }
